import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

// Actions
import { toggleExistingParticipantsDialog } from "../../actions";

// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import TableSortLabel from "@material-ui/core/TableSortLabel";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";

// Other Files
import { customTheme } from "../../constants/customTheme";
import { colors } from "../../constants/colors";
import classNames from "classnames";

const useStyles = makeStyles({
  root: (props) => ({
    ...customTheme(props.theme).dialog.root,
    width: "calc(100% - 50px)",
    maxWidth: "calc(100% - 50px)",
    height: "calc(100% - 60px)",
    padding: "25px 0px 26px 0px",
  }),
  title: customTheme().dialog.title,
  contentContainer: (props) => ({
    ...customTheme().dialog.contentContainer,
    ...customTheme(props.theme).customScrollBar,
    overflowY: "hidden",
    width: "100%",
    padding: "8px 0px",
  }),
  contentItem: {
    ...customTheme().dialog.contentItem,

    margin: "0px",
  },
  buttonContainer: customTheme().dialog.buttonContainer,
  buttonOutlined: (props) => ({
    width: "110px",
    ...customTheme(props.theme).buttonOutlined,
  }),
  tableContainer: (props) => ({
    marginTop: "15px",
    height: "calc(100% - 195px)",
    maxHeight: "calc(100% - 195px)",
    "@media only screen and (max-width: 900px)": {
      height: "calc(100% - 195px)",
      maxHeight: "calc(100% - 195px)",
    },
    overflowY: "auto",
    ...customTheme(props.theme).customScrollBar,
  }),
  subHeaderRow: {
    backgroundColor: (props) => colors[props.theme].tableHeader,
    padding: "10px 15px 10px 15px",
  },
  dataRow: {
    cursor: "pointer",
  },
  dataCell: {
    padding: "10px 15px 10px 15px",
  },
  noData: {
    textAlign: "center",
    marginTop: "50px",
  },
  tablePaperRoot: {
    height: "100%",
    width: "100%",
    borderRadius: "0px",
    boxShadow: "unset",
    backgroundColor: (props) => colors[props.theme].dialogBkg,
  },
  centerText: {
    display: "flex",
    justifyContent: "center",
    margin: "50px",
  },
  icon: customTheme().icon,
  activeSortIcon: {
    opacity: 1,
  },
  inActiveSortIcon: {
    opacity: 0.2,
  },
});

const ExistingParticipantDialog = (props) => {
  const classes = useStyles(props);
  const columnParticipants = [
    {
      title: "Participant ID",
      field: "participantId",
      minWidth: "130px",
      paddingLeft: "20px",
    },
    {
      title: "Secondary ID",
      field: "otherId",
      minWidth: "110px",
    },
    {
      title: "Birth Year",
      field: "birthYear",
      minWidth: "100px",
    },
    { title: "Skin Type", field: "skinType", minWidth: "110px" },
    {
      title: "Gender",
      field: "gender",
      minWidth: "100px",
    },
    {
      title: "Date of Enrollment",
      field: "doe",
      minWidth: "120px",
    },
  ];

  useEffect(() => {
    if (props.localParticipantLogRecevied === true) {
      setLocalState({
        ...localState,
        dataTable: formatData(props.logParticipantsById),
      });
    }
  }, [props.localParticipantLogRecevied, props.logParticipantsById]);

  const getWindowSize = () => {
    let newDataTableSize = window.innerHeight;
    if (window.innerHeight > 600) {
      newDataTableSize = window.innerHeight - 375; // These numbers are based on dialog size
    } else {
      newDataTableSize = window.innerHeight - 265; // These numbers are based on dialog size
    }
    return newDataTableSize;
  };

  useEffect(() => {
    // Measure window size and dynamicaly change the size of the data table
    const handleResize = () => {
      setLocalState({ ...localState, windowSize: getWindowSize() });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount and unmount

  const formatData = (data) => {
    let filderedParticipants = [];
    let doeData = {};
    if (
      Object.keys(props.logParticipantsByProtocolId).includes(
        props.selectedProtocolId
      )
    ) {
      filderedParticipants =
        props.logParticipantsByProtocolId[props.selectedProtocolId];

      for (let participantId of filderedParticipants) {
        Object.keys(data[participantId].studyById).map((studyId) => {
          if (Object.keys(doeData).includes(participantId)) {
            let dateNumA = Number(
              doeData[participantId].split("/")[2] +
                doeData[participantId].split("/")[0] +
                doeData[participantId].split("/")[1]
            );
            let dateNumB = Number(
              data[participantId].studyById[studyId].studyDate.split("/")[2] +
                data[participantId].studyById[studyId].studyDate.split("/")[0] +
                data[participantId].studyById[studyId].studyDate.split("/")[1]
            );
            if (dateNumB < dateNumA) {
              doeData[participantId] =
                data[participantId].studyById[studyId].studyDate;
            }
          } else {
            doeData[participantId] =
              data[participantId].studyById[studyId].studyDate;
          }
        });
      }
    }
    let dataTable = [];
    if (filderedParticipants.length !== 0) {
      filderedParticipants.forEach((participantId) => {
        let skinTypeMapped;
        switch (data[participantId].participantInfo.skinType) {
          case 1:
            skinTypeMapped = "Type I";
            break;
          case 2:
            skinTypeMapped = "Type II";
            break;
          case 3:
            skinTypeMapped = "Type III";
            break;
          case 4:
            skinTypeMapped = "Type IV";
            break;
          case 5:
            skinTypeMapped = "Type V";
            break;
          case 6:
            skinTypeMapped = "Type VI";
            break;
          default:
            skinTypeMapped = "-";
            break;
        }
        dataTable.push({
          participantId: participantId,
          birthYear:
            data[participantId].participantInfo.birthYear === ""
              ? "-"
              : data[participantId].participantInfo.birthYear,
          otherId:
            data[participantId].participantInfo.otherId === ""
              ? "-"
              : data[participantId].participantInfo.otherId,
          skinType: skinTypeMapped,
          gender:
            data[participantId].participantInfo.gender === ""
              ? "-"
              : data[participantId].participantInfo.gender,
          doe: doeData[participantId],
        });
      });
    }
    return dataTable;
  };

  const [localState, setLocalState] = React.useState({
    dataTable: formatData(props.logParticipantsById),
    windowSize: getWindowSize(),
    order: "asc",
    orderBy: null,
  });

  const requestSort = (property) => (event) => {
    const isAsc = localState.orderBy === property && localState.order === "asc";
    setLocalState({
      ...localState,
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    }); //toggle between
  };

  function descendingComparator(a, b, orderBy) {
    if (orderBy === "doe") {
      let dateNumA = Number(
        a[orderBy].split("/")[2] +
          a[orderBy].split("/")[0] +
          a[orderBy].split("/")[1]
      );
      let dateNumB = Number(
        b[orderBy].split("/")[2] +
          b[orderBy].split("/")[0] +
          b[orderBy].split("/")[1]
      );
      if (dateNumB < dateNumA) {
        return -1;
      }
      if (dateNumB > dateNumA) {
        return 1;
      }
      return 0;
    } else {
      if (b[orderBy] < a[orderBy]) {
        return -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return 1;
      }
      return 0;
    }
  }

  function stableSort(array) {
    const stabilizedThis = array.map((el, index) => [el, index]);

    stabilizedThis.sort((a, b) => {
      if (localState.order === "desc") {
        return descendingComparator(a[0], b[0], localState.orderBy);
      } else {
        return -descendingComparator(a[0], b[0], localState.orderBy);
      }
    });
    return stabilizedThis.map((el) => el[0]);
  }

  return (
    <Dialog
      open={props.open}
      PaperProps={{ className: classNames(classes.root) }}
      disableBackdropClick={true}
    >
      <Typography
        className={classes.title}
        variant="h5"
        color="textPrimary"
        align="center"
      >
        Registered Participants for {props.selectedProtocolName} Protocol
      </Typography>

      <DialogContent className={classes.contentContainer}>
        <Paper className={classes.tablePaperRoot}>
          {!props.localParticipantLogRecevied ? (
            <div className={classes.centerText}>
              <CircularProgress className={classes.icon} size={25} />
              <Typography>Loading...</Typography>
            </div>
          ) : localState.dataTable.length === 0 ? (
            <Typography className={classes.noData}>
              No participant found
            </Typography>
          ) : (
            <TableContainer
              className={classes.tableContainer}
              style={{
                maxHeight: localState.windowSize,
                height: localState.windowSize,
              }}
            >
              <Table stickyHeader>
                {/* SUBTABLE HEADER */}
                <TableHead>
                  <TableRow className={classes.subHeaderRow}>
                    {columnParticipants.map((subHeader) => (
                      <TableCell
                        className={classes.subHeaderRow}
                        key={subHeader.field}
                        align="left"
                        style={{
                          minWidth: subHeader.minWidth,

                          width: "auto",
                          paddingLeft: subHeader.paddingLeft
                            ? subHeader.paddingLeft
                            : "unset",
                        }}
                        sortDirection={
                          localState.orderBy === subHeader.field
                            ? localState.order
                            : false
                        }
                      >
                        <TableSortLabel
                          active={localState.orderBy === subHeader.field}
                          direction={
                            localState.orderBy === subHeader.field
                              ? localState.order
                              : "asc"
                          }
                          onClick={requestSort(subHeader.field)}
                          classes={{
                            // Override icon class based on activation state
                            icon:
                              localState.orderBy === subHeader.field
                                ? classes.activeSortIcon
                                : classes.inActiveSortIcon,
                          }}
                        >
                          {subHeader.title}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stableSort(localState.dataTable).map((row, index) => (
                    <TableRow
                      hover
                      tabIndex={-1}
                      role="button"
                      key={index}
                      className={classes.dataRow}
                      onClick={() => {
                        props.handleClickExistingParticipant(row.participantId);
                        props.toggleExistingParticipantsDialog();
                      }}
                    >
                      {columnParticipants.map((subHeader) => (
                        <TableCell
                          className={classes.dataCell}
                          key={subHeader.field}
                          align="left"
                          style={{
                            paddingLeft:
                              subHeader.field === "participantId"
                                ? "20px"
                                : "unset",
                          }}
                        >
                          {row[subHeader.field]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </DialogContent>

      <DialogActions className={classes.buttonContainer}>
        <Button
          onClick={() => props.onClose()}
          className={classes.buttonOutlined}
          variant="outlined"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      toggleExistingParticipantsDialog,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  return {
    logParticipantsById: state.availableParticipants.logParticipantsById,
    logParticipantsByProtocolId:
      state.availableParticipants.logParticipantsByProtocolId,
    localParticipantLogRecevied:
      state.availableParticipants.localParticipantLogRecevied,
    theme: state.utility.theme,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExistingParticipantDialog);
